import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {
    Easing,
    type LayoutChangeEvent,
    useWindowDimensions,
} from 'react-native';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import Animated, {
    runOnJS,
    runOnUI,
    scrollTo,
    useAnimatedReaction,
    useAnimatedRef,
    useAnimatedScrollHandler,
    useAnimatedStyle,
    useSharedValue,
    withTiming,
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
    BodyLargeEmphasized,
    CloseButton,
    FruitfulLogoIcon,
    isNonMobileWeb,
    useThemeColors,
    View,
    YStack,
} from 'smoothie';

import {
    ConversationalMMAnalyticsEvent,
    type ConversationalMMAnalyticsEventName,
} from '../analytics/events';
import { SlideNav } from '../components/SlideNav';
import { useConversationalMMContext } from '../context/ConversationalMMContextBase';
import AhaMoment from './AhaMoment';
import {
    CULTIVATING_FADE_MS,
    CULTIVATING_HOLD_MS,
    CultivatingScreen,
} from './CultivatingScreen';
import { MobileWebSafariGradientBackground } from './MobileWebSafariGradientBackground';

const SLIDE_DURATION = 750;
const SLIDE_EASING = Easing.out(Easing.cubic);
const SLIDE_ENTER_DURATION = 450;
const SLIDE_FADE_DURATION = 200;
const SWIPE_THRESHOLD = 40;

const PANEL_DOWN = 0;
const PANEL_UP = 1;

const Slide = ({ onTransitionGap }: { onTransitionGap?: () => void }) => {
    const { height } = useWindowDimensions();
    const { slideIndex, slides } = useConversationalMMContext();

    const [activeIndex, setActiveIndex] = useState(slideIndex);
    const slideY = useSharedValue(height);
    const slideOpacity = useSharedValue(0);

    useEffect(() => {
        slideY.value = withTiming(0, {
            duration: SLIDE_ENTER_DURATION,
            easing: SLIDE_EASING,
        });
        slideOpacity.value = withTiming(1, {
            duration: SLIDE_FADE_DURATION,
        });
    }, []);

    useEffect(() => {
        if (slideIndex === activeIndex) return;

        slideOpacity.value = withTiming(0, { duration: SLIDE_FADE_DURATION });

        const timer = setTimeout(() => {
            // Content is now invisible — reset scroll before swapping in new content
            onTransitionGap?.();

            setActiveIndex(slideIndex);
            slideY.value = 20;
            slideOpacity.value = 0;

            requestAnimationFrame(() => {
                slideY.value = withTiming(0, {
                    duration: SLIDE_FADE_DURATION,
                    easing: SLIDE_EASING,
                });
                slideOpacity.value = withTiming(1, {
                    duration: SLIDE_FADE_DURATION,
                });
            });
        }, SLIDE_FADE_DURATION);
        return () => {
            clearTimeout(timer);
        };
    }, [slideIndex, activeIndex, slideY, slideOpacity]);

    const slideAnimatedStyle = useAnimatedStyle(() => ({
        opacity: slideOpacity.value,
        transform: [{ translateY: slideY.value }],
    }));

    const slideContent = useMemo(() => {
        const slide = slides[activeIndex];
        if (slide == null) return null;
        const { Component } = slide;
        return <Component />;
    }, [activeIndex, slides]);

    return (
        <Animated.View
            style={[
                {
                    alignSelf: 'center',
                    backgroundColor: 'transparent',
                    width: '100%',
                    alignItems: 'center',
                    overflow: 'hidden',
                    minHeight: height,
                },
                slideAnimatedStyle,
            ]}
        >
            {slideContent != null && slideContent}
        </Animated.View>
    );
};

interface FullscreenResultsContentLegacyProps {
    onClose?: () => void;
    showCloseButton: boolean;
    skipCultivating: boolean;
    skipEnteringAnimation: boolean;
}

const FullscreenResultsContentLegacy = ({
    onClose,
    showCloseButton,
    skipCultivating,
    skipEnteringAnimation,
}: FullscreenResultsContentLegacyProps) => {
    const { getColor } = useThemeColors();
    const { top } = useSafeAreaInsets();
    const { height, width } = useWindowDimensions();
    const topPadding = top > 0 ? top : 16;

    const {
        ahaResult,
        slideIndex,
        setSlideIndex,
        slides,
        slideCount,
        lastIndex,
        onTrackEvent,
    } = useConversationalMMContext();
    const { displayHeadline } = ahaResult;

    const [showScrollHint, setShowScrollHint] = useState(false);
    const [resultsPanelRevealed, setResultsPanelRevealed] = useState(false);
    const [isCultivating, setIsCultivating] = useState(!skipCultivating);
    const [showAhaMoment, setShowAhaMoment] = useState(skipCultivating);
    // Separate from `showAhaMoment`: the swipe gestures stay disabled for
    // the same delay the signup flow always had, even when skipping the
    // Cultivating hold. Enabling them immediately on mount let them start
    // tracking touches during this screen's push-in transition, which
    // crashed natively (Reanimated/gesture-handler race) — content can
    // render right away, but interactivity still waits this out.
    const [gesturesEnabled, setGesturesEnabled] = useState(false);

    useEffect(() => {
        if (!skipCultivating) {
            const holdTimer = setTimeout(() => {
                setIsCultivating(false);
            }, CULTIVATING_HOLD_MS);

            const revealTimer = setTimeout(
                () => {
                    setShowAhaMoment(true);
                },
                CULTIVATING_HOLD_MS + CULTIVATING_FADE_MS / 2,
            );

            const gestureTimer = setTimeout(
                () => {
                    setGesturesEnabled(true);
                },
                CULTIVATING_HOLD_MS + CULTIVATING_FADE_MS / 2,
            );

            return () => {
                clearTimeout(holdTimer);
                clearTimeout(revealTimer);
                clearTimeout(gestureTimer);
            };
        }

        const gestureTimer = setTimeout(
            () => {
                setGesturesEnabled(true);
            },
            CULTIVATING_HOLD_MS + CULTIVATING_FADE_MS / 2,
        );

        return () => {
            clearTimeout(gestureTimer);
        };
    }, [skipCultivating]);

    useEffect(() => {
        if (!showAhaMoment) return undefined;
        const timer = setTimeout(() => {
            setShowScrollHint(true);
        }, 1500);
        return () => {
            clearTimeout(timer);
        };
    }, [showAhaMoment]);

    const panelY = useSharedValue(height);
    const panelState = useRef(PANEL_DOWN);
    const isAnimating = useRef(false);

    // Mirrors `height` on the UI thread so slideDown/the swipe gestures below
    // can read an always-current value inside their worklets without listing
    // `height` as a dependency — `useWindowDimensions` can report an updated
    // value shortly after this screen mounts (e.g. as safe-area/layout
    // settle), and having that recreate the callbacks/gestures below risks
    // the same mid-touch native handler rebuild that crashes the app.
    const heightValue = useSharedValue(height);
    useEffect(() => {
        heightValue.value = height;
    }, [height, heightValue]);

    const scrollViewRef = useAnimatedRef<Animated.ScrollView>();
    const scrollY = useSharedValue(0);
    const [navHeight, setNavHeight] = useState(0);

    const onTopNavLayout = useCallback((e: LayoutChangeEvent) => {
        setNavHeight(e.nativeEvent.layout.height);
    }, []);

    const [scrollEnabled, setScrollEnabled] = useState(false);

    const onSlideUpComplete = useCallback(() => {
        isAnimating.current = false;
        setScrollEnabled(true);
        setResultsPanelRevealed(true);
    }, []);

    const onSlideDownComplete = useCallback(() => {
        isAnimating.current = false;
    }, []);

    const slideUp = useCallback(
        (revealedBy: string) => {
            if (panelState.current === PANEL_UP || isAnimating.current) return;
            panelState.current = PANEL_UP;
            isAnimating.current = true;
            setScrollEnabled(false);
            panelY.value = withTiming(
                0,
                { duration: SLIDE_DURATION, easing: SLIDE_EASING },
                () => {
                    runOnJS(onSlideUpComplete)();
                },
            );
            onTrackEvent(ConversationalMMAnalyticsEvent.ResultsRevealed, {
                revealedBy,
            });
        },
        [panelY, onSlideUpComplete, onTrackEvent],
    );

    const slideDown = useCallback(() => {
        if (panelState.current === PANEL_DOWN || isAnimating.current) return;
        panelState.current = PANEL_DOWN;
        isAnimating.current = true;
        setScrollEnabled(false);
        panelY.value = withTiming(
            heightValue.value,
            { duration: SLIDE_DURATION, easing: SLIDE_EASING },
            () => {
                runOnJS(onSlideDownComplete)();
            },
        );
    }, [panelY, heightValue, onSlideDownComplete]);

    const slideUpFromNativeSwipe = useCallback(() => {
        slideUp('swipe-native');
    }, [slideUp]);

    // Native swipe equivalent of the web wheel/touch handlers below — reads
    // panelY (a shared value, safe on the UI thread) for a rough "which side
    // are we on" check, then defers to slideUp/slideDown's own JS-thread
    // guards (panelState/isAnimating) for the real state check.
    //
    // `.enabled()` keeps the *native* recognizer from tracking touches at
    // all until `gesturesEnabled` flips — as opposed to gating only inside
    // the worklet — because letting the recognizer track touches during
    // this screen's push-in transition is what crashed natively.
    const swipeUpGesture = useMemo(
        () =>
            Gesture.Pan()
                .enabled(gesturesEnabled)
                .onEnd((e) => {
                    if (
                        panelY.value > heightValue.value / 2 &&
                        e.translationY < -SWIPE_THRESHOLD
                    ) {
                        runOnJS(slideUpFromNativeSwipe)();
                    }
                }),
        [panelY, heightValue, slideUpFromNativeSwipe, gesturesEnabled],
    );

    const swipeDownGesture = useMemo(
        () =>
            Gesture.Pan()
                .enabled(gesturesEnabled)
                .onEnd((e) => {
                    if (
                        panelY.value < heightValue.value / 2 &&
                        e.translationY > SWIPE_THRESHOLD
                    ) {
                        runOnJS(slideDown)();
                    }
                }),
        [panelY, heightValue, slideDown, gesturesEnabled],
    );

    const panelStyle = useAnimatedStyle(
        () => ({
            transform: [{ translateY: panelY.value }],
        }),
        [],
    );

    const overlayAnimatedStyle = useAnimatedStyle(
        () => ({
            opacity: Math.max(
                0,
                Math.min(1, 1 - panelY.value / heightValue.value),
            ),
        }),
        [],
    );

    const [overlayVisible, setOverlayVisible] = useState(false);
    const [navVisible, setNavVisible] = useState(false);
    const [navAbove, setNavAbove] = useState(true);

    // Empty dependency arrays keep these reactions stable across re-renders.
    // Re-registering mid-scroll (scrollY → setNavAbove → re-render) was
    // crashing natively via Reanimated's getHostFunction assertion.
    useAnimatedReaction(
        () => panelY.value < heightValue.value * 0.85,
        (visible, prev) => {
            if (visible !== prev) {
                runOnJS(setOverlayVisible)(visible);
            }
        },
        [],
    );

    useAnimatedReaction(
        () => panelY.value < heightValue.value * 0.5,
        (visible, prev) => {
            if (visible !== prev) {
                runOnJS(setNavVisible)(visible);
            }
        },
        [],
    );

    useAnimatedReaction(
        () => scrollY.value < 10,
        (atTop, prev) => {
            if (atTop !== prev) {
                runOnJS(setNavAbove)(atTop);
            }
        },
        [],
    );

    // No dependency array here previously meant a brand-new handler was
    // created every render, forcing Reanimated to re-register the native
    // scroll event listener on every render — including mid-scroll, since
    // scrolling itself drives re-renders (via the navAbove/overlay/nav
    // reactions below). An empty array is correct: this worklet only writes
    // to the stable `scrollY` shared value and closes over nothing else.
    const scrollHandler = useAnimatedScrollHandler(
        {
            onScroll: (event) => {
                scrollY.value = event.contentOffset.y;
            },
        },
        [],
    );

    useEffect(() => {
        // window exists as a bare global on native (React Native sets up a
        // minimal shim for web-oriented code) but has no addEventListener —
        // this whole gesture handler is web-only, so check for that directly.
        if (
            typeof window === 'undefined' ||
            typeof window.addEventListener !== 'function'
        ) {
            return;
        }

        let touchStartY = 0;
        let touchTriggered = false;

        const handleWheel = (e: WheelEvent) => {
            if (isAnimating.current) {
                e.preventDefault();
                return;
            }

            if (panelState.current === PANEL_DOWN && e.deltaY > 0) {
                e.preventDefault();
                slideUp('scroll');
            }
        };

        const handleTouchStart = (e: TouchEvent) => {
            touchStartY = e.touches[0]?.clientY ?? 0;
            touchTriggered = false;
        };

        const handleTouchMove = (e: TouchEvent) => {
            if (touchTriggered || isAnimating.current) return;
            const currentY = e.touches[0]?.clientY ?? 0;
            const deltaY = currentY - touchStartY;

            if (panelState.current === PANEL_DOWN && deltaY < -30) {
                touchTriggered = true;
                slideUp('swipe');
            }
        };

        window.addEventListener('wheel', handleWheel, { passive: false });
        window.addEventListener('touchstart', handleTouchStart, {
            passive: true,
        });
        window.addEventListener('touchmove', handleTouchMove, {
            passive: false,
        });

        return () => {
            window.removeEventListener('wheel', handleWheel);
            window.removeEventListener('touchstart', handleTouchStart);
            window.removeEventListener('touchmove', handleTouchMove);
        };
    }, [slideUp, slideDown, scrollY]);

    useEffect(() => {
        if (!resultsPanelRevealed) return;
        const slide = slides[slideIndex];
        onTrackEvent(
            ConversationalMMAnalyticsEvent.ResultsSlideViewed,
            {
                slide: slide?.name ?? '',
                slideIndex,
            },
            { sendImmediately: true },
        );
    }, [resultsPanelRevealed, slideIndex, slides, onTrackEvent]);

    useEffect(() => {
        panelY.value = withTiming(panelY.value === 0 ? 0 : height, {
            duration: 0,
        });
    }, [height]);

    const resetScrollInstantly = useCallback(() => {
        runOnUI(() => {
            'worklet';
            scrollTo(scrollViewRef, 0, 0, false);
        })();
        scrollY.value = 0;
    }, [scrollViewRef, scrollY]);

    const trackPrevNextPressed = useCallback(
        (
            fromSlideIndex: number,
            eventName: ConversationalMMAnalyticsEventName,
        ) => {
            const fromSlide = slides[fromSlideIndex];
            onTrackEvent(eventName, {
                fromSlide: fromSlide?.name ?? '',
                fromSlideIndex,
            });
        },
        [slides, onTrackEvent],
    );

    const onPrevious = () => {
        trackPrevNextPressed(
            slideIndex,
            ConversationalMMAnalyticsEvent.ResultsPrev,
        );
        setSlideIndex(Math.max(0, slideIndex - 1));
    };
    const onNext = () => {
        trackPrevNextPressed(
            slideIndex,
            ConversationalMMAnalyticsEvent.ResultsNext,
        );
        setSlideIndex(Math.min(lastIndex, slideIndex + 1));
    };

    const maxWidth = useMemo(() => (width > 770 ? 560 : width - 16), [width]);

    return (
        <View flex={1}>
            <MobileWebSafariGradientBackground
                overlayVisible={overlayVisible}
            />
            {isNonMobileWeb && !isCultivating && (
                <View position="absolute" top={40} left={40} zIndex={2}>
                    <FruitfulLogoIcon
                        width={40}
                        height={34}
                        color={getColor('$leaf')}
                    />
                </View>
            )}
            {showCloseButton && (
                <View
                    position="absolute"
                    top={isNonMobileWeb ? 40 : topPadding + 8}
                    right={isNonMobileWeb ? 40 : 16}
                    // Always above the results scroll panel (zIndex 3).
                    // navAbove drops to 2 after scroll, which left this
                    // under the full-screen panel and unclickable.
                    zIndex={5}
                >
                    <CloseButton background="gray" onPress={onClose} />
                </View>
            )}
            {/* header nav */}
            <GestureDetector gesture={swipeDownGesture}>
                <View
                    position="absolute"
                    top={topPadding}
                    left={0}
                    right={0}
                    zIndex={navAbove ? 4 : 2}
                    alignItems="center"
                    onLayout={onTopNavLayout}
                    opacity={navVisible ? 1 : 0}
                    pointerEvents={navVisible ? 'auto' : 'none'}
                    style={{
                        transition: 'opacity 0.4s ease',
                    }}
                >
                    <YStack
                        gap={8}
                        alignItems="center"
                        justifyContent="center"
                        paddingHorizontal={32}
                    >
                        <BodyLargeEmphasized textAlign="center">
                            {displayHeadline}
                            {ahaResult.type === 'investing'
                                ? ` ${ahaResult.projectedDate}`
                                : ahaResult.debtFreeDate !== '' &&
                                  ` ${ahaResult.debtFreeDate}`}
                        </BodyLargeEmphasized>
                        <SlideNav
                            index={slideIndex}
                            onNext={onNext}
                            onPrevious={onPrevious}
                            slideCount={slideCount}
                            lastIndex={lastIndex}
                        />
                    </YStack>
                </View>
            </GestureDetector>

            {/* aha moment */}
            <GestureDetector gesture={swipeUpGesture}>
                <View
                    flex={1}
                    alignItems="center"
                    alignSelf="center"
                    paddingTop={topPadding}
                    maxWidth={maxWidth}
                >
                    {showAhaMoment && (
                        <AhaMoment
                            ahaResult={ahaResult}
                            navVisible={navVisible}
                            showScrollHint={showScrollHint}
                            skipEnteringAnimation={skipEnteringAnimation}
                        />
                    )}
                </View>
            </GestureDetector>

            {!skipCultivating && <CultivatingScreen visible={isCultivating} />}

            {/* overlay between aha moment and scroll container */}
            <Animated.View
                pointerEvents="none"
                style={[
                    {
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 1,
                        backgroundColor: 'rgba(18, 18, 18, 0.15)',
                    },
                    overlayAnimatedStyle,
                ]}
            />

            {/* scroll container with slides */}
            <Animated.View
                style={[
                    {
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height,
                        zIndex: 3,
                    },
                    panelStyle,
                ]}
            >
                <Animated.ScrollView
                    ref={scrollViewRef}
                    onScroll={scrollHandler}
                    scrollEventThrottle={16}
                    alwaysBounceVertical={false}
                    bounces={false}
                    overScrollMode="never"
                    scrollEnabled={scrollEnabled}
                    style={{ flex: 1 }}
                    contentContainerStyle={{
                        paddingTop: topPadding + navHeight + 16,
                        paddingBottom: 86,
                        width: maxWidth,
                        maxWidth,
                        alignSelf: 'center',
                    }}
                    showsVerticalScrollIndicator={false}
                >
                    <Slide onTransitionGap={resetScrollInstantly} />
                </Animated.ScrollView>
            </Animated.View>
        </View>
    );
};

export default FullscreenResultsContentLegacy;
